// @ts-check
import cloneDeep from 'lodash/cloneDeep';
import orderBy from 'lodash/orderBy';
import _omit from 'lodash/omit';
import _defaults from 'lodash/defaults';
import { getAdjustedPrice } from '../utils';
import { GIFT_NOTE_TYPES, PACKAGING_TYPES } from '../../../constants/giftOptions';
import { getPercentOff } from '../../products/selectors';

/**
 * @typedef {import('../../../types').GroupClient} GroupClient
 */

/**
 * @typedef {Object} price
 * @property {string} [currentPrice]
 * @property {string} [currencyCode]
 * @property {string} [itemPriceEach]
 * @property {string} [itemPriceExt]
 * @property {string} [itemTax]
 * @property {string} [itemTaxEach]
 * @property {string} [itemDeliveryPrice]
 * @property {string} [itemOtherPrice]
 * @property {string} [itemDutyPrice]
 * @property {string} [adjustedPrice]
 * @property {string} [priceOverrideReason]
 * @property {string} [priceOverrideReasonDetails]
 * @property {string} [orderPrice]
 */

/**
 * @typedef {Object} customizations
 * @property {string} [giftWrapCode]
 * @property {string} [giftCard]
 * @property {string} [giftMessage1]
 * @property {string} [giftMessage2]
 * @property {string} [giftMessage3]
 * @property {string} [giftMessage4]
 * @property {string} [giftMessage5]
 * @property {boolean} [giftReceipt]
 * @property {string} [adjustmentValue]
 * @property {string} [priceType]
 * @property {string} [adjustedPrice]
 * @property {string} [priceOverrideReason]
 * @property {string} [priceOverrideReasonDetails]
 */

/**
 * @typedef {Object} giftOptions
 * @property {string} [giftWrapCode]
 * @property {string} [giftCard]
 * @property {string} [giftMessage1]
 * @property {string} [giftMessage2]
 * @property {string} [giftMessage3]
 * @property {string} [giftMessage4]
 * @property {string} [giftMessage5]
 * @property {boolean} [giftReceipt]
 */

/**
 * @typedef {Object} clientConnectsItem
 * @property {string} timestamp
 * @property {string} associatePin
 * @property {boolean} completed
 * @property {string []} actions
 */

/**
 * @param {string} originalPrice
 * @param {*} percentOff
 */
const getPromoPrice = (originalPrice, percentOff) => {
  if (!originalPrice || !percentOff) return null;
  return (parseFloat(originalPrice) * (100 - percentOff) * 0.01).toFixed(2);
};

/**
 * @param {customizations} customizations
 * @param {giftOptions} giftOptions
 */
const getGiftOptions = (customizations, giftOptions) => ({
  giftWrapCode: customizations.giftWrapCode || giftOptions?.giftWrapCode || PACKAGING_TYPES.NO_PACKAGING,
  giftCard: customizations.giftCard || giftOptions?.giftCard || GIFT_NOTE_TYPES.NO_GIFT_NOTE,
  giftMessage1: customizations.giftMessage1 || giftOptions?.giftMessage1 || '',
  giftMessage2: customizations.giftMessage2 || giftOptions?.giftMessage2 || '',
  giftMessage3: customizations.giftMessage3 || giftOptions?.giftMessage3 || '',
  giftMessage4: customizations.giftMessage4 || giftOptions?.giftMessage4 || '',
  giftMessage5: customizations.giftMessage5 || giftOptions?.giftMessage5 || '',
  giftReceipt: customizations.giftReceipt || giftOptions?.giftReceipt || false,
});

/**
 * @param {price} price
 * @param {string | null} promoPrice
 * @param {customizations} customizations
 */
const getFullPrice = (price, promoPrice, customizations) => ({
  currentPrice: price?.currentPrice,
  currencyCode: price?.currencyCode,
  promoPrice,
  adjustedPromoPrice: getAdjustedPrice(promoPrice, customizations.adjustmentValue, customizations.priceType),
  itemPriceEach: price?.itemPriceEach || '',
  itemPriceExt: price?.itemPriceExt || '',
  itemTax: price?.itemTax || '',
  itemTaxEach: price?.itemTaxEach || '',
  itemDeliveryPrice: price?.itemDeliveryPrice || '',
  itemOtherPrice: price?.itemOtherPrice || '',
  itemDutyPrice: price?.itemDutyPrice || '',
  adjustedPrice: customizations.adjustedPrice || price?.adjustedPrice || '',
  priceOverrideReason: customizations.priceOverrideReason || price?.priceOverrideReason || '',
  priceOverrideReasonDetails: customizations.priceOverrideReasonDetails || price?.priceOverrideReasonDetails || '',
  orderPrice: price?.orderPrice || '',
});

const productFlags = ['backorderFlag', 'dropshipFlag', 'giftWrappableFlag'];
const fieldsToOmit = ['paddedInventory', 'personalization', 'store', 'skuStores', 'customizations'];

const defaultProductValues = {
  size: '',
  inventory: 0,
  quantity: 0,
  createdBy: '',
  status: '',
  trackingNumber: '',
  dropshipCategoryId: '',
  promotionsApplied: [],
};

/**
 * @typedef {Object} product
 * @param {product} product
 */
export const mapProduct = (product = {}) => {
  const customizations = product.customizations || {};
  // calculated fields
  const percentOff = getPercentOff(product);
  const promoPrice = getPromoPrice(product.price?.currentPrice, percentOff);
  const fullPrice = getFullPrice(product.price, promoPrice, customizations);
  const giftOptions = getGiftOptions(customizations, product.giftOptions);
  // product flags
  const isBackorder = product.backorderFlag === 'Y' && product.inventory === 0;
  const isDropship = product.dropshipFlag === 'Y';
  const isGiftWrappable = product.giftWrappableFlag === 'Y';
  const { isParenthetical, parentheticalCharge } = product;

  const formatParentheticalCharge = (charge = 0) => (charge % 1 !== 0 ? charge.toFixed(2) : charge);

  const addedProductFields = {
    isBackorder,
    isDropship,
    isGiftWrappable,
    percentOff,
    price: fullPrice,
    giftOptions,
    isParenthetical,
    parentheticalCharge: parentheticalCharge ? formatParentheticalCharge(parentheticalCharge) : 0,
  };

  const nextProduct = _omit(product, [...productFlags, ...fieldsToOmit]);

  return Object.assign(_defaults(nextProduct, defaultProductValues), addedProductFields);
};

export const sortRTCDataCollection = (rtcDataCollection) => orderBy(rtcDataCollection, ['completed'], ['asc']);

/**
 * @param {clientConnectsItem []} currentDataCollection
 * @param {clientConnectsItem []|clientConnectsItem} updatedItems
 * @return {clientConnectsItem[]}
 */
export const getRTCUpdatedDataClone = (currentDataCollection, updatedItems = []) => {
  const dataClone = cloneDeep(currentDataCollection);
  const updatedCollection = updatedItems instanceof Array ? updatedItems : [updatedItems];

  updatedCollection.forEach(({ timestamp, completed }) => {
    const targetDataObject = dataClone.find((dataItem) => dataItem.timestamp === timestamp);
    if (targetDataObject) {
      targetDataObject.completed = completed;
    }
  });

  return sortRTCDataCollection(dataClone);
};

export const splitPlccAndNoPlccKiNumbers = (loyaltyAssets, plccKiNumbers, nonPlccKiNumbers) =>
  Object.entries(loyaltyAssets).reduce(
    (acc, [key, value]) => {
      if (plccKiNumbers.includes(key)) {
        acc.loyaltyAssets[key] = value;
      }
      if (nonPlccKiNumbers.includes(key)) {
        acc.nonPlccAssets[key] = value;
      }

      return acc;
    },
    { loyaltyAssets: {}, nonPlccAssets: {} },
  );

/**
 * @param {clientConnectsItem} updatedClientConnect
 * @param {clientConnectsItem []} existedConnects
 * @return {clientConnectsItem[]}
 */
export const getUpdatedClientConnectsOnCompletedChange = (updatedClientConnect, existedConnects) => {
  if (!updatedClientConnect) return existedConnects;
  if (!existedConnects || !existedConnects?.length) {
    return existedConnects;
  }
  const { timestamp, completed } = updatedClientConnect;
  return existedConnects.map((connect) => {
    if (connect.timestamp === timestamp) {
      return {
        ...connect,
        completed,
      };
    }
    return connect;
  });
};

/**
 *
 * @param {{[key: string]: GroupClient}} clients
 * @param {number} index
 * @return {{[key: string]: GroupClient}} clientsCopy
 */
export const getClientsWithoutOneAtIndex = (clients, index) => {
  const clientsCopy = cloneDeep(clients);
  delete clientsCopy[index];
  return clientsCopy;
};
